import React, { useEffect, useState } from 'react';
import './webstoreCartModal.scss';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const WebstoreCartModal = ({ items, user, onRemove, onClose, onClearCart, onUserUpdate }) => {
  const [note, setNote] = useState('');
  const [cooldownInfo, setCooldownInfo] = useState(null);
  const total = items.reduce((sum, item) => sum + item.price, 0);
  const canAfford = user && user.njCredits >= total;

  const canCheckout =
    user &&
    canAfford &&
    cooldownInfo &&
    cooldownInfo.remainingOrders > 0 &&
    items.length > 0 &&
    items.length <= cooldownInfo.remainingOrders;

  useEffect(() => {
    const fetchCooldownInfo = async () => {
      if (!user) return;

      try {
        const res = await axios.get(`${API_BASE_URL}/api/webstore-orders/cooldown?email=${user.email}`);
        setCooldownInfo(res.data);
      } catch (err) {
        console.error('Cooldown check failed:', err);
        setCooldownInfo({ remainingOrders: 0, nextAvailable: null });
      }
    };

    fetchCooldownInfo();
  }, [user]);

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please log in to complete your purchase.');
      return;
    }

    if (!canAfford) {
      toast.error('Insufficient NJ credits to complete this order.');
      return;
    }

    if (items.length === 0) {
      toast.error('Your cart is empty.');
      return;
    }

    if (cooldownInfo && cooldownInfo.remainingOrders <= 0) {
      toast.error(`Order limit reached. Please wait until ${dayjs(cooldownInfo.nextAvailable).format('MMM D, h:mm A')}.`);
      return;
    }

    if (cooldownInfo && items.length > cooldownInfo.remainingOrders) {
      toast.error(`You can only order up to ${cooldownInfo.remainingOrders} item(s) right now.`);
      return;
    }

    try {
      const payload = {
        user: {
          _id: user._id,
          username: user.username,
          name: user.name,
          email: user.email,
          contactNumber: user.contactNumber,
          address: user.address,
        },
        items: items.map((item) => ({
          _id: item._id,
          title: item.title,
          category: item.category,
          price: item.price,
          deliveryTime: item.deliveryTime,
          images: item.images,
        })),
        total,
        note,
      };

      const res = await axios.post(`${API_BASE_URL}/api/webstore-orders`, payload);
      toast.success('Order placed successfully!');

      if (typeof onClearCart === 'function') {
        onClearCart();
      }

      if (typeof onUserUpdate === 'function') {
        onUserUpdate((prev) => ({
          ...prev,
          njCredits: prev.njCredits - total,
        }));
      }

      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to place order. Please try again.');
    }
  };

  return (
    <div className="cart-modal-overlay" onClick={onClose}>
      <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <h2>Your Cart</h2>
          <button className="cart-close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {items.length === 0 ? (
          <p className="empty-text">Your cart is empty.</p>
        ) : (
          <>
            <ul className="cart-items">
              {items.map((item, index) => (
                <li className="cart-item" key={index}>
                  {item.images?.[0] && (
                    <img src={item.images[0]} alt={item.title} className="item-image" />
                  )}
                  <div className="item-info">
                    <h4>{item.title}</h4>
                    <p>₱{item.price.toFixed(2)}</p>
                  </div>
                  <button className="remove-item-btn" onClick={() => onRemove(index)}>Remove</button>
                </li>
              ))}
            </ul>

            <div className="summary">
              <p>Total: <strong>₱{total.toFixed(2)}</strong></p>
              {user && (
                <p>Your NJ Credits: <strong>₱{user.njCredits.toFixed(2)}</strong></p>
              )}
              {cooldownInfo && (
                <div className="cooldown-info">
                  <p>
                    Orders remaining: <strong>{cooldownInfo.remainingOrders}/3</strong>
                  </p>
                  {cooldownInfo.remainingOrders <= 0 && (
                    <p className="cooldown-warning">
                      Cooldown active. Next order available:{' '}
                      <strong>{dayjs(cooldownInfo.nextAvailable).format('MMM D, h:mm A')}</strong>
                    </p>
                  )}
                </div>
              )}

              {cooldownInfo && items.length > cooldownInfo.remainingOrders && (
                <p className="warning-text">
                  You can only order up to <strong>{cooldownInfo.remainingOrders}</strong> item{cooldownInfo.remainingOrders > 1 ? 's' : ''} at this time.
                  Please remove {items.length - cooldownInfo.remainingOrders} item{items.length - cooldownInfo.remainingOrders > 1 ? 's' : ''}.
                </p>
              )}

              <textarea
                placeholder="Add note or special instructions..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="note-input"
              />

              <button
                className="confirm-btn"
                onClick={handleCheckout}
                disabled={!canCheckout}
              >
                Confirm Order
              </button>

              {!canAfford && user && (
                <p className="warning-text">Not enough NJcredits to place the order.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WebstoreCartModal;
